import { createSlice } from '@reduxjs/toolkit';
import { authApi } from '../../services/authApi';

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  // Whether initialization (reading localStorage + token validation) completed
  initialized: false,
  error: null,
  refreshToken: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.loading = false;
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    loginUserAction: (state, action) => {
      const { user, token, refreshToken } = action.payload;
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;

      // Update refreshToken if provided
      if (refreshToken) {
        state.refreshToken = refreshToken;
        localStorage.setItem('refreshToken', refreshToken);
      }

      // Persist to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    },
    initializeAuth: (state) => {
      // Don't initialize from localStorage here - let the validation thunk handle it
      // This prevents race conditions where invalid tokens are loaded
    },
    setInitialized: (state, action) => {
      state.initialized = action.payload === undefined ? true : action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addMatcher(authApi.endpoints.login.matchPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;

        // RTK Query may return the server body as { success, message, data: { user, token, refreshToken } }
        // or sometimes as the inner data directly. Normalize both shapes here.
        const payloadData = action.payload?.data || action.payload;
        const user = payloadData?.user || payloadData;
        const token = payloadData?.token || payloadData?.accessToken || state.token;
        const refreshToken = payloadData?.refreshToken || state.refreshToken;

        state.user = user || null;
        state.token = token || null;
        state.refreshToken = refreshToken || null;
        state.error = null;

        // Persist to localStorage
        if (token) localStorage.setItem('token', token);
        if (refreshToken !== undefined) localStorage.setItem('refreshToken', refreshToken || '');
        if (user) localStorage.setItem('user', JSON.stringify(user));
      })
      .addMatcher(authApi.endpoints.login.matchRejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload?.data?.message || 'Login failed';
      })

      // Register
      .addMatcher(authApi.endpoints.register.matchPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.register.matchFulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addMatcher(authApi.endpoints.register.matchRejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.data?.message || 'Registration failed';
      })

      // Logout
      .addMatcher(authApi.endpoints.logout.matchPending, (state) => {
        state.loading = true;
      })
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.error = null;
        state.loading = false;
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      })

      // Refresh Token
      .addMatcher(authApi.endpoints.refreshToken.matchFulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('refreshToken', action.payload.refreshToken || '');
      })

      // Get Profile
      .addMatcher(authApi.endpoints.getProfile.matchFulfilled, (state, action) => {
        state.user = action.payload.data?.user || action.payload.user || action.payload;
        localStorage.setItem('user', JSON.stringify(state.user));
      })

      // Update Profile
      .addMatcher(authApi.endpoints.updateProfile.matchFulfilled, (state, action) => {
        state.user = action.payload.data?.user || action.payload.user || action.payload;
        localStorage.setItem('user', JSON.stringify(state.user));
      });
  },
});

export const {
  logout,
  clearError,
  setLoading,
  loginUserAction,
  initializeAuth,
  setInitialized,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectToken = (state) => state.auth.token;
export const selectAuthInitialized = (state) => state.auth.initialized;

// Thunks for additional auth logic
export const initializeAuthState = () => (dispatch) => {
  dispatch(initializeAuthWithValidation());
};

export const initializeAuthWithValidation = () => async (dispatch) => {
  console.log('Initializing auth with validation...');
  // Set loading state
  dispatch(setLoading(true));

  // Load from localStorage first
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');
  const userString = localStorage.getItem('user');

  console.log('LocalStorage data:', { token: !!token, refreshToken: !!refreshToken, userString: !!userString });

  if (token && userString && userString !== 'undefined' && userString !== null) {
    try {
      const user = JSON.parse(userString);
      if (user && typeof user === 'object') {
        console.log('Setting initial auth state from localStorage');
        // Set initial state from localStorage
        dispatch(loginUserAction({ user, token, refreshToken }));

        // Then validate the token
        try {
          console.log('Validating token with profile API...');
          const result = await dispatch(
            authApi.endpoints.getProfile.initiate()
          ).unwrap();
          console.log('Token validation successful');

          // Normalize result: RTK Query may return { success, data: { user } } or the user directly
          const profilePayload = result?.data || result;
          const freshUser = profilePayload?.user || profilePayload;

          // If we got a fresh user object, update state with it
          dispatch(loginUserAction({ user: freshUser, token, refreshToken }));
        } catch (error) {
          console.warn('Token validation failed, attempting refresh:', error);

          // If token validation fails, try to refresh the token
          if (refreshToken) {
            try {
              console.log('Attempting token refresh...');
              const refreshResult = await dispatch(
                authApi.endpoints.refreshToken.initiate(refreshToken)
              ).unwrap();

              // If refresh successful, get profile again
              const profileResult = await dispatch(
                authApi.endpoints.getProfile.initiate()
              ).unwrap();

              const profilePayload2 = profileResult?.data || profileResult;
              const refreshedUser = profilePayload2?.user || profilePayload2;

              dispatch(loginUserAction({
                user: refreshedUser,
                token: refreshResult.token,
                refreshToken: refreshResult.refreshToken
              }));
              console.log('Token refresh successful');
            } catch (refreshError) {
              console.warn('Token refresh failed, clearing auth data:', refreshError);
              // If refresh also fails, clear auth state
              dispatch(logout());
            }
          } else {
            // No refresh token available, clear auth state
            dispatch(logout());
          }
        }
      } else {
        // Invalid user data, clear localStorage
        console.log('Invalid user data, clearing localStorage');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }
    } catch (parseError) {
      // Clear invalid data from localStorage
      console.log('Parse error, clearing localStorage');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      console.warn('Invalid user data in localStorage, cleared auth data');
    }
  } else {
    console.log('No valid localStorage data found');
  }

  // Clear loading state
  dispatch(setLoading(false));
  console.log('Auth initialization complete');
  // Mark initialization finished
  dispatch(setInitialized(true));
};

export const loginUser = (credentials) => async (dispatch) => {
  try {
    const result = await dispatch(
      authApi.endpoints.login.initiate(credentials)
    ).unwrap();
    return result;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const registerUser = (userData) => async (dispatch) => {
  try {
    const result = await dispatch(
      authApi.endpoints.register.initiate(userData)
    ).unwrap();
    return result;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await dispatch(authApi.endpoints.logout.initiate()).unwrap();
  } catch (error) {
    console.error('Logout error:', error);
    // Even if logout fails, clear local state
    dispatch(logout());
  }
};

export const refreshUserToken = () => async (dispatch, getState) => {
  try {
    const refreshToken = getState().auth.refreshToken;
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const result = await dispatch(
      authApi.endpoints.refreshToken.initiate(refreshToken)
    ).unwrap();
    return result;
  } catch (error) {
    console.error('Token refresh error:', error);
    // If refresh fails, logout user
    dispatch(logout());
    throw error;
  }
};
